import { Injectable } from '@angular/core';
import { Observable, map, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { RestapiService } from '@app/services/restapi.service';
import {
  PmsMappingCategoryCreate,
  RestPmsMappingCategoryModel,
} from '@main-application/management/interfaces/pms-mapping-category';
import {
  PmsMappingTopicCreate,
  PmsMappingTopicUpdate,
} from '@main-application/management/interfaces/pms-mapping-topic';
import { RestTimezone } from '@main-application/management/pages/system-configuration/components/date-time-configuration/model/timezone';
import { TimezoneEntityHelper } from '@main-application/management/pages/system-configuration/components/timezone-entity.helper';
import { AddAnswerReqI } from '@main-application/service/pages/canned-answer.interface';
import { CommonInMemoryUpdatingService } from '@main-application/shared/services/common-in-memory-updating.service';
import {
  SummaryReportOffline,
  TicketsSummaryReportQueryParams,
} from '@main-application/shared/services/summary-report-settings.service';
import { PermissionLevelType } from '@shared/enums/permission-level.enum';
import { PriorityType } from '@shared/enums/priority-type';
import { checkPermissionLevel } from '@shared/functions/check-permission-level.function';
import { filterToQueryParams } from '@shared/functions/filter-to-query-params.function';
import {
  RestTicketCategoryModel,
  RestTicketModel,
  RestTicketSubCategoryModel,
  RestTypedTicketAttachment,
  UpdateTicketAssigneeModel,
  UpdateTypedAttachmentToEntityModel,
} from '@shared/interfaces/turnover.interface';
import { UserData } from '@shared/interfaces/user-data';
import { SnackbarErrorMessage } from '@ui-components/components/customized-snackbar/snackbar-message.enum';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';
import {
  ServiceSummaryStatsDetails,
  SummaryFilterItem,
  SummaryStatsGroup,
} from '@ui-components/components/summary-expandable-table/summary-expandable-table.model';
import { DialogResult } from '@ui-components/modals/config/dialog-result.enum';

import { CacheService } from './cache.service';
import { PeriodDateSearchFields } from './summary.service';

export const TicketsControllerKeys = {
  TicketTopic: 'TicketTopic',
  TicketCategory: 'TicketCategory',
};

export class TicketFilter {
  assignedToMe?: boolean;
  includeCompleted?: boolean;
  startDate?: string;
  endDate?: string;
  propertyId?: number;
  propertyIds?: number[];
  portfolioId?: number;
  ticketType?: number;
  isCompleted?: number;
  unitType?: number | string;
  ticketReportingCategoryId?: string;
  ticketCategoryId?: number;
  ticketTopicId?: number;
  ticketSummaryType?: number;
  collectionIds?: number;
  boardId?: number;
  periodDateSearchFields?: PeriodDateSearchFields;
  serviceByStatusFilter?: TicketByStatusFilter;
  filters?: string;
}

export interface TicketListQueryParams {
  serviceByStatusFilter?: TicketByStatusFilter;
  periodDateSearchFields?: PeriodDateSearchFields;

  portfolioId?: number;
  collectionIds?: string;
  propertyIds?: string;

  templateIds?: string;

  ticketCategoryIds?: string;
  ticketTopicIds?: string;
  boardIds?: string;
  boardTypes?: string;
  unitType?: number;
  unitTypes?: string;
  ticketType?: number;
  reportingCategoryIds?: string;
  reportingTopicIds?: string;
  priority?: PriorityType;
  priorities?: string;
  scheduleIds?: string;
  activityIds?: string;
  assigneeIds?: string;
  turnCurrentStepAssigneeIds?: string;
  clientServiceRateAsSatisfiedIds?: string;

  startDate?: string;
  endDate?: string;

  filters?: SummaryFilterItem[];

  assignedToMe?: boolean;
  includeCompleted?: boolean;
  createdFrom?: string;
  createdTo?: string;
  residentTickets?: boolean;
  canEditAll?: boolean;
  canEdit?: boolean;
  canEditUnassigned?: boolean;
  ticketSummaryType?: number;
  bbColumnName?: string;
  modifiedSince?: string;
  includeTranslations?: boolean;
}

export const periodDateSearchFields = [
  { value: PeriodDateSearchFields.AnyTimeframe, label: 'Any Timeframe' },
  { value: PeriodDateSearchFields.DueDateOnly, label: 'Due Date Only' },
  { value: PeriodDateSearchFields.CreatedAtDateOnly, label: 'Created At Date Only' },
];

export enum TicketByStatusFilter {
  Reopened,
  ReopenedLate,
  Due,
  DueLate,
  WaitingParts,
  WaitingPartsLate,
  InProgress,
  InProgressLate,
  Completed,
  CompletedLate,
  Submitted,
  NotStarted,
  Closed,
  TodoStatusCategoryTotal,
  InProgressStatusCategoryTotal,
  PendingStatusCategoryTotal,
  CompletedStatusCategoryTotal,
  Total,
  Pending,
  TodoStatusCategoryTotalLate,
  NonCompletedTotal,
  InProgressStatusCategoryTotalLate,
  PendingStatusCategoryTotalLate,
  CompletedStatusCategoryTotalLate,
  TotalLate,
  Upcoming,
  NoDue,
  DueToday,
  Due24h,
  Due48h,
  Due72hPlus,
  CompletedOnTime,
  CompletedLate24h,
  CompletedLate48h,
  CompletedLate72hPlus,
  OpenLessThan30Days,
  Open30DaysPlus,
  TodayDueCompleted,
  TotalFeedbackCollected,
}

export const TicketByStatusFilterLabels: Record<TicketByStatusFilter, string> = {
  [TicketByStatusFilter.Reopened]: 'Reopened',
  [TicketByStatusFilter.ReopenedLate]: 'Reopened (late)',
  [TicketByStatusFilter.Due]: 'Due',
  [TicketByStatusFilter.DueLate]: 'Due (late)',
  [TicketByStatusFilter.WaitingParts]: 'Waiting parts',
  [TicketByStatusFilter.WaitingPartsLate]: 'Waiting parts (late)',
  [TicketByStatusFilter.InProgress]: 'In Progress',
  [TicketByStatusFilter.InProgressLate]: 'In Progress (late)',
  [TicketByStatusFilter.Completed]: 'Completed',
  [TicketByStatusFilter.CompletedLate]: 'Completed (late)',
  [TicketByStatusFilter.Submitted]: 'Submitted',
  [TicketByStatusFilter.NotStarted]: 'Not Started',
  [TicketByStatusFilter.Closed]: 'Closed',
  [TicketByStatusFilter.TodoStatusCategoryTotal]: 'To do',
  [TicketByStatusFilter.InProgressStatusCategoryTotal]: 'In Progress',
  [TicketByStatusFilter.PendingStatusCategoryTotal]: 'Pending',
  [TicketByStatusFilter.CompletedStatusCategoryTotal]: 'Completed',
  [TicketByStatusFilter.Total]: 'Total',
  [TicketByStatusFilter.Pending]: 'Pending',
  [TicketByStatusFilter.TodoStatusCategoryTotalLate]: 'To do (late)',
  [TicketByStatusFilter.NonCompletedTotal]: 'Incomplete',
  [TicketByStatusFilter.InProgressStatusCategoryTotalLate]: 'In Progress (late)',
  [TicketByStatusFilter.PendingStatusCategoryTotalLate]: 'Pending (late)',
  [TicketByStatusFilter.CompletedStatusCategoryTotalLate]: 'Completed (late)',
  [TicketByStatusFilter.TotalLate]: 'Total (late)',
  [TicketByStatusFilter.Upcoming]: 'Upcoming',
  [TicketByStatusFilter.NoDue]: 'No due date',
  [TicketByStatusFilter.DueToday]: 'Due today',
  [TicketByStatusFilter.Due24h]: 'Due in 24 hours',
  [TicketByStatusFilter.Due48h]: 'Due in 48 hours',
  [TicketByStatusFilter.Due72hPlus]: 'Due in 72 or more hours',
  [TicketByStatusFilter.CompletedOnTime]: 'Completed on time',
  [TicketByStatusFilter.CompletedLate24h]: 'Completed (24 hours late)',
  [TicketByStatusFilter.CompletedLate48h]: 'Completed (48 hours late)',
  [TicketByStatusFilter.CompletedLate72hPlus]: 'Completed (72 or more hours late)',
  [TicketByStatusFilter.OpenLessThan30Days]: 'Open < 30 days',
  [TicketByStatusFilter.Open30DaysPlus]: 'Open ≥ 30 days',
  [TicketByStatusFilter.TodayDueCompleted]: 'Due today (completed)',
  [TicketByStatusFilter.TotalFeedbackCollected]: 'Feedback Collected',
};

export const TicketByStatusFilterToSimpleText: Partial<Record<TicketByStatusFilter, string>> = {
  [TicketByStatusFilter.TodoStatusCategoryTotal]: 'ToDo',
  [TicketByStatusFilter.TodoStatusCategoryTotalLate]: 'ToDo',
  [TicketByStatusFilter.InProgressStatusCategoryTotal]: 'InProgress',
  [TicketByStatusFilter.InProgressStatusCategoryTotalLate]: 'InProgress',
  [TicketByStatusFilter.PendingStatusCategoryTotal]: 'WaitingParts',
  [TicketByStatusFilter.PendingStatusCategoryTotalLate]: 'WaitingParts',
  [TicketByStatusFilter.CompletedStatusCategoryTotal]: 'Resolved',
  [TicketByStatusFilter.CompletedStatusCategoryTotalLate]: 'Resolved',
  [TicketByStatusFilter.Reopened]: 'Reopened',
  [TicketByStatusFilter.ReopenedLate]: 'Reopened',
  [TicketByStatusFilter.WaitingParts]: 'WaitingParts',
  [TicketByStatusFilter.WaitingPartsLate]: 'WaitingParts',
  [TicketByStatusFilter.InProgress]: 'InProgress',
  [TicketByStatusFilter.InProgressLate]: 'InProgress',
  [TicketByStatusFilter.Submitted]: 'Submitted',
  [TicketByStatusFilter.NotStarted]: 'NotStarted',
  [TicketByStatusFilter.Closed]: 'Closed',
};

export enum ServiceSummaryGroupType {
  None,
  TicketCategory,
  TicketTopic,
  PropertyGroup,
  Property,
  Portfolio,
  UnitType,
}

export enum ServiceSummaryGroupV2Type {
  None,
  ReportingCategory,
  ReportingTopic,
  PropertyGroup,
  Property,
  Portfolio,
  UnitType,
  Priority,
  Board,
  Task,
  TicketCategory,
  TicketTopic,
  ClientServiceRateAsSatisfied,
  Assignee = 99,
}

export enum ServiceSummaryTicketType {
  AllTickets,
  ServiceTickets,
  ResidentTickets,
  InspectionTickets,
  ReplacementTickets,
}

export interface SummaryFilters {
  portfolioId?: number;
  topicCategoryId?: number;
  topicId?: number;
  collectionIds?: string;
  propertyIds?: string;
  unitType?: number;
  startDate?: string;
  endDate?: string;
  ticketType?: ServiceSummaryTicketType;
  groupBy?: ServiceSummaryGroupType;
  subGroupBy?: ServiceSummaryGroupType;
}

export interface RestSubmitFeedbackModel {
  ticketId: number;
  clientServiceRateAsSatisfied: boolean | null;
  clientFeedbackComment?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class TicketsService extends CommonInMemoryUpdatingService<RestTicketModel, TicketFilter> {
  constructor(
    private restApiService: RestapiService,
    private cacheService: CacheService,
    private snackbarService: SnackbarService
  ) {
    super();
  }

  create(turnoverTicket: RestTicketModel, residentMode = false): Observable<RestTicketModel> {
    return this.restApiService.create<RestTicketModel>(`Tickets?residentTickets=${residentMode}`, turnoverTicket);
  }

  update(turnoverTicket: RestTicketModel): Observable<RestTicketModel> {
    return this.restApiService.update<RestTicketModel>(`Tickets/${turnoverTicket.id}`, turnoverTicket);
  }

  ticketsChangeAssignee(model: UpdateTicketAssigneeModel): Observable<RestTicketModel> {
    return this.restApiService.create<RestTicketModel>(`Tickets/changeAssignee`, model);
  }

  protected deleteInternal(ticketId: number): Observable<void> {
    return this.restApiService.delete(`Tickets/${ticketId}`);
  }

  residentList(): Observable<RestTicketModel[]> {
    return this.restApiService.getData<RestTicketModel[]>(`Tickets?residentTickets=${true}`);
  }

  protected getListInternal(filter: any): Observable<RestTicketModel[]> {
    const filterString = filterToQueryParams(filter);
    return this.restApiService
      .getData<RestTicketModel[]>(`Tickets${filterString}`, TicketsService.fixTimezoneForRestTicketList)
      .pipe(
        catchError(err => {
          this.snackbarService.error(SnackbarErrorMessage.RetrievingTicketList);
          return of([]);
        })
      );
  }

  updateItemInMemory(updatedItem: RestTicketModel) {
    this.updateSavedData(list => list.map(item => (item.id === updatedItem.id ? updatedItem : item)));
  }

  rate(ticketId: number, clientFeedbackRating: number): Observable<RestTicketModel[]> {
    return this.restApiService.post<RestTicketModel[]>(`Tickets/submitfeedback`, {
      ticketId: ticketId,
      clientFeedbackRating: clientFeedbackRating,
    });
  }

  getTicket(ticketId: number): Observable<RestTicketModel> {
    return this.restApiService.getData<RestTicketModel>(
      `Tickets/${ticketId}`,
      TicketsService.fixTimezoneForRestTicketModel
    );
  }

  getTicketSafe(ticketId: number): Observable<{ result: DialogResult; ticket?: RestTicketModel; error?: any }> {
    return this.getTicket(ticketId).pipe(
      map(ticket => ({ result: DialogResult.Success, ticket })),
      catchError(err => {
        const errorMsg = err?.error?.errorCode
          ? err.error.errorCode
          : `Failed to load ticket #${ticketId}. Please try again.`;
        this.snackbarService.error(errorMsg);
        return of({ result: DialogResult.Fail, error: err });
      })
    );
  }

  getTicketAttachment(ticketId: number): Observable<RestTypedTicketAttachment[]> {
    return this.restApiService.getData<RestTypedTicketAttachment[]>(`Tickets/${ticketId}/Attachments`);
  }

  setAttachment(attachmentToEntity: UpdateTypedAttachmentToEntityModel): Observable<RestTypedTicketAttachment> {
    return this.restApiService.create<RestTypedTicketAttachment>(
      `Tickets/${attachmentToEntity.ticketId}/Attachments`,
      attachmentToEntity
    );
  }

  getTicketsByTurnover(turnoverId: number): Observable<RestTicketModel[]> {
    return this.restApiService.getData<RestTicketModel[]>(
      `Tickets/ByTurnoverId/${turnoverId}`,
      TicketsService.fixTimezoneForRestTicketList
    );
  }

  public static fixTimezoneForRestTicketList(data: RestTicketModel[], timezone: RestTimezone): RestTicketModel[] {
    if (!data) return [];
    data.forEach(e => TimezoneEntityHelper.fixTimezoneForRestTicketDataModel(e, timezone));
    return data;
  }

  public static fixTimezoneForRestTicketModel(data: RestTicketModel, timezone: RestTimezone): RestTicketModel {
    return TimezoneEntityHelper.fixTimezoneForRestTicketDataModel(data, timezone);
  }

  getTicketsByAssigneeId(assigneeId: number): Observable<RestTicketModel[]> {
    return this.restApiService.getData<RestTicketModel[]>(`Tickets/ByAssigneeId/${assigneeId}`);
  }

  getTicketsByPropertyId(propertyId: number): Observable<RestTicketModel[]> {
    return this.restApiService.getData<RestTicketModel[]>(`Tickets/ByPropertyId/${propertyId}`);
  }

  deleteTurnoverAttachment(ticketId: number, attachmentId: number): Observable<boolean> {
    return this.restApiService.delete(`Tickets/${ticketId}/Attachments/${attachmentId}`);
  }

  getCashedCategories(propertyId = 0): Observable<RestTicketCategoryModel[]> {
    return this.cacheService.getCachedInfo('getCategories', propertyId, () => this.getCategories(propertyId));
  }

  checkPermissionToEdit(userData: UserData, ticket?: RestTicketModel, notify = false): boolean {
    const result =
      ticket?.createdById === userData.id ||
      checkPermissionLevel(userData.permissionLevel, PermissionLevelType.Tickets_EditAll) ||
      (ticket?.assigneeId === userData?.id &&
        checkPermissionLevel(userData.permissionLevel, PermissionLevelType.Tickets_Edit));

    if (notify && !result) {
      this.snackbarService.warning('Tickets_EditAll permission required');
    }

    return result;
  }

  getCategories(propertyId = 0): Observable<RestTicketCategoryModel[]> {
    return this.restApiService.getData<RestTicketCategoryModel[]>(
      `TicketPickupList${propertyId ? `?propertyId=${propertyId}` : ''}`
    );
  }

  createCategory(body: PmsMappingCategoryCreate) {
    return this.restApiService.post<PmsMappingCategoryCreate>(TicketsControllerKeys.TicketCategory, body);
  }

  updateCategory(body: RestPmsMappingCategoryModel) {
    return this.restApiService.update(`${TicketsControllerKeys.TicketCategory}/${body.id}`, body);
  }

  deleteCategory(id: number) {
    return this.restApiService.delete(`${TicketsControllerKeys.TicketCategory}/${id}`);
  }

  getTicketCategoryList() {
    return this.restApiService.getData<RestTicketCategoryModel[]>(`TicketCategory/list`);
  }

  ticketTopicList(): Observable<RestTicketSubCategoryModel[]> {
    return this.restApiService.getData<RestTicketSubCategoryModel[]>(`${TicketsControllerKeys.TicketTopic}/list`);
  }

  createTopic(body: PmsMappingTopicCreate) {
    return this.restApiService.post<PmsMappingTopicCreate>(TicketsControllerKeys.TicketTopic, body);
  }

  updateTopic(body: PmsMappingTopicUpdate, id: number) {
    return this.restApiService.update(`${TicketsControllerKeys.TicketTopic}/${id}`, body);
  }

  deleteTopic(id: number) {
    return this.restApiService.delete(`${TicketsControllerKeys.TicketTopic}/${id}`);
  }

  convertToSeconds(timeString: string): number {
    const [days, hours, minutes] = timeString.split('::').map(Number);
    return days * 86400 + hours * 3600 + minutes * 60;
  }

  convertToTimeSpent(seconds: number): string {
    const days = Math.floor(seconds / 86400);
    seconds %= 86400;
    const hours = Math.floor(seconds / 3600);
    seconds %= 3600;
    const minutes = Math.floor(seconds / 60);
    return `${days}::${hours}::${minutes}`;
  }

  formatCurrency(value: number): string {
    if (!value || value === null) return null;
    const parts = value.toString().split('.');
    const wholePart = parts[0];
    const fractionalPart = parts.length > 1 ? '.' + parts[1] : '';
    return '$' + wholePart.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + fractionalPart;
  }

  parseCurrency(value: string): number {
    if (!value || value === null || value === '0') return null;
    return parseFloat(value.replace(/[^0-9.-]+/g, ''));
  }

  getAnswers(): Observable<any> {
    return this.restApiService.getData<any>(`CannedAnswer`, TicketsService.fixTimezoneForRestTicketModel);
  }

  addAnswer(body: AddAnswerReqI): Observable<void> {
    return this.restApiService.create('CannedAnswer', body);
  }

  editAnswer(text: string, id: number): Observable<void> {
    return this.restApiService.update(`CannedAnswer/${id}`, { text });
  }

  deleteAnswer(id: number): Observable<void> {
    return this.restApiService.delete(`CannedAnswer/${id}`);
  }

  getServiceSummaryStats(filter: SummaryFilters): Observable<SummaryStatsGroup[]> {
    let url = `tickets/summaryStats/${filter.groupBy ?? 0}/${filter.subGroupBy ?? 0}?`;

    if (filter.portfolioId !== null && filter.portfolioId !== undefined) url += `&portfolioId=${filter.portfolioId}`;
    if (filter.collectionIds) url += `&collectionIds=${filter.collectionIds}`;
    if (filter.propertyIds) url += `&propertyIds=${filter.propertyIds}`;
    if (filter.unitType !== null && filter.unitType !== undefined) url += `&unitType=${filter.unitType}`;
    if (filter.ticketType !== null && filter.ticketType !== undefined) url += `&ticketType=${filter.ticketType}`;
    if (filter.topicCategoryId !== null && filter.topicCategoryId !== undefined)
      url += `&topicCategoryId=${filter.topicCategoryId}`;
    if (filter.topicId !== null && filter.topicId !== undefined) url += `&topicId=${filter.topicId}`;
    if (filter.startDate) url += `&startDate=${filter.startDate}`;
    if (filter.endDate) url += `&endDate=${filter.endDate}`;

    return this.restApiService.getData<SummaryStatsGroup[]>(url);
  }

  getServiceSummaryDetails(filter: SummaryFilters): Observable<ServiceSummaryStatsDetails> {
    let url = `tickets/summaryStatsDetails?`;

    if (filter.portfolioId !== null && filter.portfolioId !== undefined) url += `&portfolioId=${filter.portfolioId}`;
    if (filter.collectionIds) url += `&collectionIds=${filter.collectionIds}`;
    if (filter.propertyIds) url += `&propertyIds=${filter.propertyIds}`;
    if (filter.unitType !== null && filter.unitType !== undefined) url += `&unitType=${filter.unitType}`;
    if (filter.ticketType !== null && filter.ticketType !== undefined) url += `&ticketType=${filter.ticketType}`;
    if (filter.topicCategoryId !== null && filter.topicCategoryId !== undefined)
      url += `&topicCategoryId=${filter.topicCategoryId}`;
    if (filter.topicId !== null && filter.topicId !== undefined) url += `&topicId=${filter.topicId}`;
    if (filter.startDate) url += `&startDate=${filter.startDate}`;
    if (filter.endDate) url += `&endDate=${filter.endDate}`;

    return this.restApiService.getData<ServiceSummaryStatsDetails>(url).pipe(
      map(data => {
        data.statsByAssignee = data.statsByAssignee.map(el => ({ ...el, isAssignee: true }));
        data.statsByTopic = data.statsByTopic.map(el => ({ ...el, isTopic: true }));
        data.statsByCategory = data.statsByCategory.map(el => ({ ...el, isCategory: true }));
        return data;
      })
    );
  }

  getTicketsList(filter: TicketListQueryParams): Observable<RestTicketModel[]> {
    return this.restApiService.post<RestTicketModel[]>('tickets/list', filter).pipe(
      catchError(err => {
        const error = err?.errorCode ?? SnackbarErrorMessage.RetrievingTicketList;
        this.snackbarService.error(error);
        return of([]);
      })
    );
  }

  downloadTicketsSummaryReport(settings: TicketsSummaryReportQueryParams): Observable<SummaryReportOffline> {
    return this.restApiService.post<SummaryReportOffline>('tickets/exportSummary/offline', settings);
  }

  submitFeedback(
    model: RestSubmitFeedbackModel,
    ticketName: string
  ): Observable<{ result: DialogResult; error?: any }> {
    return this.restApiService.post<void>('tickets/submitFeedback', model).pipe(
      map(() => ({ result: DialogResult.Success })),
      catchError(err => {
        const errorMsg = err?.error?.message ? err.error.message : `Failed to update ${ticketName}. Please try again.`;
        this.snackbarService.error(errorMsg);
        return of({ result: DialogResult.Fail, error: err });
      })
    );
  }
}
